/**
 * @file JobsHelpers.js
 * @fileoverview API functions which are called by the jobs redux thunks.
 *
 * @typedef {import('./JobDefinition').JobStatusOptions} JobStatusOptions
 * @typedef {import('./JobDefinition').JobTypeOptions} JobTypeOptions
 */

import { hasLogin } from 'BMapsSrc/server/session_request';
import { WebServices } from 'BMapsSrc/WebServices';
import { JobManagerRequestTypes } from './jobs_protocol';

/**
 * @typedef {{
 *    jobid: string
 *    jobtype: JobTypeOptions
 *    state: JobStatusOptions,
 *    requestmeta: object,
 *    stash: object,
 *    jobrequest: object,
 *    type: "Job"
 * }[]} getJobsResponse
 *
 * @returns {Promise<{ results: getJobsResponse, error: string }>}
 */
export async function getJobs() {
    let results = null;
    let error = '';
    if (await hasLogin()) {
        try {
            const url = `${WebServices.JOBS_URL}get-jobs`;
            const data = await WebServices.startWsRequest(url);
            results = JSON.parse(data);
            for (const job of results) {
                if (job.requestmeta) job.requestmeta = parseRequestMeta(job.requestmeta);
            }
        } catch (errorCaught) {
            console.warn(errorCaught);
            error = 'Error getting jobs list.';
        }
    } else {
        error = 'Jobs requires a BMaps login.';
    }
    return { results, error };
}

/**
 * @typedef {{
 *  type: "JobCreated",
 *  jobid: string,
 *  jobtype: JobTypeOptions,
 *  state: "starting",
 *  requestmeta: object,
 *  created: string,
 * }} postJobResponse
 *
 * @returns {Promise<{ results: postJobResponse, error: string }>}
 */
export async function postJob(data=null) {
    let results = null;
    let error = '';

    if (await hasLogin()) {
        try {
            const url = `${WebServices.JOBS_URL}start-job`;
            results = await WebServices.startWsRequest(url, {
                postData: JSON.stringify(data),
                contentType: 'application/json',
            });
            results = JSON.parse(results);
            if (results.requestmeta) {
                results.requestmeta = parseRequestMeta(results.requestmeta);
            }
        } catch (errorCaught) {
            console.warn(errorCaught);
            error = 'Error starting job.';
        }
    } else {
        error = 'Jobs requires a BMaps login.';
    }
    return { results, error };
}

/**
 *
 * @typedef {{
 * type: "JobDeleted",
 * success: boolean,
 * }} deleteJobResponse
 *
 * @returns {Promise<{ results: deleteJobResponse, error: string }>}
 */
export async function deleteJob(jobid) {
    let results = null;
    let error = '';
    if (await hasLogin()) {
        try {
            const url = `${WebServices.JOBS_URL}delete-job`;
            const data = await WebServices.startWsRequest(url, {
                postData: JSON.stringify({ jobid, type: JobManagerRequestTypes.DELETE_JOB }),
                contentType: 'application/json',
            });
            results = JSON.parse(data);
        } catch (errorCaught) {
            console.warn(errorCaught);
            error = 'Error deleting job.';
        }
    } else {
        error = 'Jobs requires a BMaps login.';
    }
    return { results, error };
}

// export async function cancelJob(userid, jobid) {
//     let results = null;
//     let error = '';
//     if (await hasLogin()) {
//         try {
//             const url = `${WebServices.JOBS_URL}/command?userid=${userid}`;
//             const formData = new FormData();
//             formData.append('userid', userid);
//             formData.append('jobid', jobid);
//             formData.append('op', 'cancel-job');
//             const data = await WebServices.startWsRequest(url, {
// postData: formData });
//             results = JSON.parse(data);
//         } catch (errorCaught) {
//             error = errorCaught;
//         }
//     } else {
//         error = 'Jobs requires a BMaps login.';
//     }
//     return { results, error };
// }

/**
 *
 * @typedef {{
 * jobid: string,
 * state: JobStatusOptions,
 * jobrequest: object,
 * stash: object,
 * jobresult: object,
 * }} loadJobResponse
 *
 * @returns {Promise<{ results: loadJobResponse, error: string }>}
 */
export async function loadJobResult(jobid) {
    let results = null;
    let error = '';
    if (await hasLogin()) {
        try {
            const url = `${WebServices.JOBS_URL}retrieve-job-results`;
            const postData = JSON.stringify(
                { jobid, type: JobManagerRequestTypes.RETRIEVE_JOB_RESULTS }
            );
            const data = await WebServices.startWsRequest(url, { postData });
            const response = JSON.parse(data);
            if (response.jobresult) {
                results = {
                    jobResults: response.jobresult,
                    jobrequest: response.jobrequest,
                    stash: response.stash,
                };
            }
            if (response.error) {
                if (response.error.report) {
                    error = response.error.report;
                    console.warn(`Received job error: ${error}`);
                }
                if (response.error.detail) {
                    console.warn(`Received job error detail: ${response.error.detail}`);
                }
            }
        } catch (errorCaught) {
            console.warn(errorCaught);
            error = 'Error retrieving job results.';
        }
    } else {
        error = 'Jobs requires a BMaps login.';
    }
    return { results, error };
}

function parseRequestMeta(requestmeta) {
    if (typeof requestmeta === 'string') {
        try {
            return JSON.parse(requestmeta);
        } catch (errorCaught) {
            console.warn(errorCaught);
            return {};
        }
    }
    return requestmeta;
}
